import { useCallback, useEffect, useState } from "react";
import { PageModule, SurveyQuestion } from "../../../../../types/working-model";
import { updateTranslateKey } from "../../../../../utils/utils";
import { TypographyItem } from "../../../../general-ui/typography-item/typography-item";
import classNames from 'classnames';
import Textarea from "../../../../general-ui/textarea/textarea";
import { getStylingOverrides } from "../../../utils";
import { ParamsProps } from "../../../live-event";
import { useParams } from "react-router";
import { TemplateClassNames } from "../../../../../types/template-layouts";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { CreateSurveyAnswer } from "../../../../../connection/save-survey-answer";
import { useDispatch } from "react-redux";
import { setCompletedTextQuestion } from "../../../../../store/actions/event/event-actions";
import { getStorageItem } from "../../../../../utils/local-storage";
import { useTranslation } from "react-i18next";

interface ITextSurveyQuestionProps {
	question: SurveyQuestion;
	module: PageModule;
	template: TemplateClassNames;
	sessionUuid: string;
}

const TextSurveyQuestion: React.FC<ITextSurveyQuestionProps> = ({ question, module, template, sessionUuid }) => {
	const { event, sessions, registrationId, userSession, registration_on, surveyTextCompleted, workingSession, previewMode } = useTypedSelector(state => ({
		event: state.LiveEventReducer.eventBundle?.event,
		sessions: state.LiveEventReducer.eventBundle?.sessions,
		registrationId: state.LiveEventReducer.registrationId,
		userSession: state.LiveEventReducer.userSession,
		registration_on: state.LiveEventReducer.eventBundle?.registration_on,
		surveyTextCompleted: state.LiveEventReducer.surveyTextCompleted,
		workingSession: state.CreateSessionReducer.workingSession,
		previewMode: state.CreateEventReducer.previewMode
	}));
	const [value, setValue] = useState('');
	const params: ParamsProps = useParams();
	const language = params?.language;
	const { content, styling_overrides } = module;
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		primaryButtonOverrides,
	} = stylingOverrides;
	const [submitDisabled, setSubmitDisabled] = useState(false);

	const dispatch = useDispatch();
	const { t } = useTranslation("session");

	const token = getStorageItem('Bl-Profile-Token');


	const getSession = useCallback(() => {
		return sessions?.find(session => session.uuid === sessionUuid);
	}, [sessions, sessionUuid]);

	const session = previewMode === 'session' ? workingSession : getSession();

	useEffect(() => {
		// this is the inital call to grab any submission for this question that's in the store
		if (surveyTextCompleted.length) {
			for (let i = 0; i < surveyTextCompleted.length; i++) {
				if (surveyTextCompleted[i].question === question.survey_question) {
					setValue(surveyTextCompleted[i].answer);
					setSubmitDisabled(true);
				}
			}
		}
	}, [surveyTextCompleted, question.survey_question]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (question.required && !value.trim().length) return;
		const translateKeyValues = updateTranslateKey({
			translateString: { base: '', changed: '' },
			input: value,
			baseLanguage: language, //because this will be end user submited, we want whichever language they are on to be the base
			language
		});

		let body = {};
		// if registration for the event is turned on, submit the
		// userSession and registrationId as well
		if (registration_on) {
			body = {
				session: session?.session,
				survey_question: question.survey_question,
				module: module.id,
				event: event,
				text_answer: translateKeyValues,
				radio_answer: null,
				checkbox_answer: null,
				session_uuid: session?.uuid,
				language: language,
				userSession: userSession,
				registration_id: registrationId
			};
		} else {
			body = {
				session: session?.session,
				survey_question: question.survey_question,
				module: module.id,
				event: event,
				text_answer: translateKeyValues,
				radio_answer: null,
				checkbox_answer: null,
				session_uuid: session?.uuid,
				language: language
			};
		}

		// update store with answer and disbale submit button
		CreateSurveyAnswer(body, token);
		setSubmitDisabled(true);
		dispatch(setCompletedTextQuestion(question.survey_question, value));
	};

	const handleValueChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setValue(e.target.value);
	};

	if (!session && (previewMode !== 'session')) return null;

	return (
		<form onSubmit={handleSubmit} className="question-container">
			<TypographyItem className={classNames('question-title evt-heading-4', template)} tagName="p">
				{question?.title?.[language] ?? question?.title?.base}
			</TypographyItem>
			<div className="survey-text-actions-container">
				<Textarea placeholder={t("Enter your repsonse here")} defaultValue={value ?? ''} onChange={handleValueChange} disabled={submitDisabled || (previewMode === 'session')} shouldUpdateValue={true} />
				<div className={classNames('submit-action')}><button className={classNames('primary next', primaryButtonOverrides)} type="submit" onClick={handleSubmit} disabled={submitDisabled || !value}>{content?.callToAction?.button_name?.[language] || t("Submit")}</button></div>
			</div>
		</form>
	);
};

export default TextSurveyQuestion;
